// ─────────────────────────────────────────────────────────────────────
//  NewsAppify sales configuration — shared by the staff dashboard,
//  leads, and templates pages so everything uses the same words.
// ─────────────────────────────────────────────────────────────────────

/** Pipeline stages for a town / newspaper deal. 'qualified' is kept so
 *  leads created before this change still show up. */
export const STAGES = [
  { key: 'new', label: 'New', open: true },
  { key: 'contacted', label: 'Contacted', open: true },
  { key: 'qualified', label: 'Qualified', open: true },
  { key: 'demo', label: 'Demo scheduled', open: true },
  { key: 'proposal', label: 'Proposal sent', open: true },
  { key: 'review', label: 'Board / council review', open: true },
  { key: 'won', label: 'Won', open: false },
  { key: 'lost', label: 'Lost', open: false },
]
export const OPEN_STAGES = STAGES.filter(s => s.open).map(s => s.key)
export const stageLabel = (k) => (STAGES.find(s => s.key === k) || {}).label || k || '—'

/** The people in a town who decide, influence or sign. */
export const CONTACT_ROLES = [
  { group: 'Town leadership', roles: ['Mayor', 'First Selectman / Select Board Chair', 'City / Town Council Member'] },
  { group: 'Town administration', roles: ['Town / City Manager', 'Town Administrator', 'City / Town Clerk', 'Communications Director / PIO', 'Finance Director', 'Procurement Officer', 'IT Director'] },
  { group: 'Local news', roles: ['Newspaper Publisher / Owner', 'Editor-in-Chief', 'Managing Editor', 'Advertising Director'] },
  { group: 'Community', roles: ['Chamber of Commerce Director', 'School Superintendent', 'Library Director', 'Community Organization Leader'] },
  { group: 'Other', roles: ['Other'] },
]
export const ALL_ROLES = CONTACT_ROLES.flatMap(g => g.roles)

/** Who a template is written for. */
export const AUDIENCES = [
  'Mayor / Town leadership',
  'Town Manager / Administrator',
  'Clerk / Communications',
  'Finance / Procurement',
  'Newspaper Publisher / Editor',
  'Chamber / Civic group',
  'Superintendent / Schools',
  'Any contact',
]

export const ORG_TYPES = [
  'City / Town Government',
  'Local Newspaper / Publication',
  'Community Organization',
  'School District',
  'Chamber of Commerce',
  'Other',
]

export const POPULATIONS = [
  { label: 'Under 10,000', plan: 'Community' },
  { label: '10,000 – 50,000', plan: 'Town' },
  { label: '50,000+', plan: 'City' },
  { label: 'Not sure', plan: '' },
]

export const PLANS = [
  { key: 'Community', price: '$399/mo', yearly: '$3,990/yr', setup: '$1,500 setup (waived yearly)', emails: '25,000 emails/mo' },
  { key: 'Town', price: '$799/mo', yearly: '$7,990/yr', setup: '$2,000 setup (waived yearly)', emails: '100,000 emails/mo' },
  { key: 'City', price: 'from $1,499/mo', yearly: 'custom yearly', setup: '$2,500 setup (waived yearly)', emails: '300,000 emails/mo' },
]

export const LEAD_SOURCES = ['Demo request', 'Referral', 'Cold outreach', 'Conference / event', 'Inbound call', 'Other']

export const NEXT_ACTIONS = [
  { key: 'call', label: 'Call' },
  { key: 'email', label: 'Email' },
  { key: 'meeting', label: 'Meeting' },
  { key: 'demo', label: 'Demo' },
  { key: 'proposal', label: 'Proposal' },
  { key: 'board', label: 'Board / council meeting' },
]

/** Placeholders a template can use, and what fills them. */
export const TEMPLATE_VARS = [
  { token: '[Contact Name]', key: 'contactName', label: 'Contact name', placeholder: 'Jane Smith' },
  { token: '[Title]', key: 'title', label: 'Their title', placeholder: 'Town Manager' },
  { token: '[Town Name]', key: 'town', label: 'Town name', placeholder: 'Springfield' },
  { token: '[Publication Name]', key: 'publication', label: 'Publication name', placeholder: 'Springfield Gazette' },
  { token: '[Sample App Link]', key: 'sampleLink', label: 'Sample app link', placeholder: 'https://…' },
  { token: '[Your Full Name]', key: 'yourName', label: 'Your full name', placeholder: 'Alex Rivera' },
  { token: '[Your Phone]', key: 'yourPhone', label: 'Your phone', placeholder: '(555) 555-5555' },
]

export function fillTemplate(text, vars) {
  let out = String(text || '').replace(/\r\n/g, '\n')
  TEMPLATE_VARS.forEach(v => { if (vars && vars[v.key]) out = out.split(v.token).join(vars[v.key]) })
  return out
}

/** Points for the team scoreboard. */
export const POINTS = { createdLead: 10, call: 5, email: 3, meeting: 10, demo: 15, proposal: 12, review: 15, won: 50, convertedRequest: 15 }

export const BADGES = [
  { name: 'Rookie', test: s => s.points >= 50 },
  { name: 'Prospector', test: s => s.points >= 250 },
  { name: 'Town Crier', test: s => (s.demos || 0) >= 5 },
  { name: 'Closer', test: s => (s.wins || 0) >= 1 },
  { name: 'Rainmaker', test: s => s.points >= 1000 },
]

/** Starter templates an admin can load with one click (AdminTemplates). */
export const STARTER_TEMPLATES = [
  {
    title: 'First outreach — Mayor / First Selectman',
    category: 'First outreach',
    audience: 'Mayor / Town leadership',
    description: 'Short, civic-minded opener for the town\'s top elected official.',
    subjectLines: [
      'A direct line from [Town Name] to every resident\'s phone',
      'Idea for keeping [Town Name] residents informed',
      '[Town Name]\'s own news app — quick question',
    ],
    body: `Dear [Title] [Contact Name],

Residents of [Town Name] hear about road closures, meetings and emergencies through a mix of social media, flyers and word of mouth — and too many of them miss things that matter.

NewsAppify gives [Town Name] its own branded iPhone and Android app, website and email newsletters, so news reaches residents directly: breaking-news alerts in about a minute, a weekday Morning Edition, and event reminders that bring people out.

We build everything and keep it running. Plans for a town your size start at $399 a month, and every plan begins with a 30-day free trial.

I've put together a sample app with [Town Name]'s name on it: [Sample App Link]

Would you have 15 minutes in the next two weeks to see it?

Respectfully,
[Your Full Name]
NewsAppify
[Your Phone]`,
  },
  {
    title: 'First outreach — Town Manager / Administrator',
    category: 'First outreach',
    audience: 'Town Manager / Administrator',
    description: 'Practical, time-saving angle for the person who runs day-to-day operations.',
    subjectLines: [
      'One place for [Town Name] news, alerts and newsletters',
      'Cutting the work of getting the word out in [Town Name]',
    ],
    body: `Hi [Contact Name],

Getting the word out in [Town Name] probably means posting the same update in four places — the website, social media, an email list and sometimes print.

NewsAppify replaces that with one newsroom. Your staff posts once and it goes to a branded [Town Name] app, the website and the right email newsletter automatically. Urgent items go out as alerts to every phone with the app, with no per-message fees.

We handle the build, the app store listings and hosting — no IT project on your side, and most towns are live in two to four weeks.

Here's a sample built for [Town Name]: [Sample App Link]

Could we find 15 minutes this month?

Best,
[Your Full Name]
[Your Phone]`,
  },
  {
    title: 'First outreach — Clerk / Communications Director',
    category: 'First outreach',
    audience: 'Clerk / Communications',
    description: 'For the person who actually posts notices, agendas and alerts.',
    subjectLines: [
      'Posting notices once instead of four times',
      'Meeting reminders that residents actually see',
    ],
    body: `Hi [Contact Name],

You're likely the person who posts [Town Name]'s meeting notices, closures and announcements — often in several places at once.

With NewsAppify you'd write it once in a simple newsroom dashboard and it appears in [Town Name]'s own app, on the website and in the next email newsletter. Residents can RSVP to events and get a reminder the day before, and you can see which updates people actually open.

I made a sample for [Town Name] here: [Sample App Link]

Happy to walk you through it — even 15 minutes would do.

Thanks,
[Your Full Name]
[Your Phone]`,
  },
  {
    title: 'First outreach — Newspaper Publisher / Editor',
    category: 'First outreach',
    audience: 'Newspaper Publisher / Editor',
    description: 'For a local paper that wants readers on mobile without a big build.',
    subjectLines: [
      '[Publication Name] on every phone in [Town Name]',
      'Reaching readers who no longer buy print',
      'A mobile edition for [Publication Name] — without the $30k build',
    ],
    body: `Hi [Contact Name],

[Publication Name] has something no national outlet can match — the trust of [Town Name]. The challenge is reaching readers who now get everything on their phones.

NewsAppify turns [Publication Name] into native iPhone and Android apps under your name, a fast website and nine automatic newsletters — Morning Edition, Sports Friday, Obituaries and more. Your newsroom keeps writing; stories go everywhere at once, and breaking news reaches every phone with the app in about a minute.

A custom app usually runs $15,000–$50,000. NewsAppify starts at $399 a month with a 30-day free trial, and we do the building.

Here's [Publication Name] as an app: [Sample App Link]

Worth 15 minutes to take a look?

Best,
[Your Full Name]
[Your Phone]`,
  },
  {
    title: 'First outreach — Chamber / civic group',
    category: 'First outreach',
    audience: 'Chamber / Civic group',
    description: 'For chambers of commerce and community organizations.',
    subjectLines: ['Keeping [Town Name] members informed and showing up', 'An app for [Town Name]\'s community calendar'],
    body: `Hi [Contact Name],

Events only work when people know about them. NewsAppify gives your organization a branded app, website and newsletters with a community calendar, RSVPs and automatic reminders the day before each event.

Members can submit their own events for your approval, and you can see who's going.

Sample for [Town Name]: [Sample App Link]

Would a short demo be useful?

Thanks,
[Your Full Name]
[Your Phone]`,
  },
  {
    title: 'Follow-up — no reply after first email',
    category: 'Follow-up',
    audience: 'Any contact',
    description: 'Send 4–6 business days after the first outreach.',
    subjectLines: ['Re: [Town Name]\'s own news app', 'Quick follow-up, [Contact Name]'],
    body: `Hi [Contact Name],

Just bringing this back to the top of your inbox. The sample app I built for [Town Name] is still here: [Sample App Link]

If the timing isn't right, or someone else handles communications for [Town Name], I'd be grateful if you could point me to them.

Thank you,
[Your Full Name]
[Your Phone]`,
  },
  {
    title: 'After the demo — recap and next step',
    category: 'After demo',
    audience: 'Any contact',
    description: 'Send the same day as the demo.',
    subjectLines: ['Thank you — [Town Name] demo recap', 'Next steps for [Town Name]\'s app'],
    body: `Hi [Contact Name],

Thank you for your time today. A quick recap of what we covered:

• [Town Name]'s own iPhone and Android apps, website and email newsletters, branded as yours
• Breaking-news alerts to every phone with the app, with no per-message fees
• Event RSVPs with automatic reminders, and resident submissions you approve
• We build it, host it and keep it running — live in two to four weeks

Your sample app: [Sample App Link]

The next step is a 30-day free trial. If it helps, I can join a council or board meeting to answer questions.

Best,
[Your Full Name]
[Your Phone]`,
  },
  {
    title: 'Proposal and pricing',
    category: 'Proposal & pricing',
    audience: 'Finance / Procurement',
    description: 'Clear pricing for the person who approves the spend.',
    subjectLines: ['NewsAppify proposal for [Town Name]', '[Town Name] — pricing and terms'],
    body: `Hi [Contact Name],

Here are the details for [Town Name]:

Plans (every feature included on every plan)
• Community — under 10,000 residents: $399/month or $3,990/year, 25,000 newsletter emails a month
• Town — 10,000 to 50,000 residents: $799/month or $7,990/year, 100,000 emails a month
• City — 50,000+ residents: from $1,499/month, 300,000 emails a month

One-time setup: $1,500 / $2,000 / $2,500, waived with yearly billing.
Every plan starts with a 30-day free trial. Monthly plans cancel anytime.
Apple requires apps to be published from [Town Name]'s own developer account ($99/year; Apple offers fee waivers for government entities). We set it up with you.

For comparison, a custom-built app typically costs $15,000–$50,000, and printing one edition can run $500–$2,000.

I'm happy to provide a W-9, a formal quote or anything your procurement process needs.

Best,
[Your Full Name]
[Your Phone]`,
  },
  {
    title: 'Budget cycle — plan for next fiscal year',
    category: 'Budget & approval',
    audience: 'Mayor / Town leadership',
    description: 'For towns that like the idea but need to budget for it.',
    subjectLines: ['Including [Town Name]\'s app in next year\'s budget', 'Budget line for [Town Name] communications'],
    body: `Hi [Contact Name],

I understand [Town Name] may need to plan for this in the next budget. To make that easy, here's a one-line summary you can use:

"Resident communications platform (branded mobile app, website and email newsletters) — NewsAppify, $7,990/year, setup included."

Adjust the plan to [Town Name]'s size if needed. I'm also glad to present to the council or select board — a 10-minute overview is usually enough.

Best,
[Your Full Name]
[Your Phone]`,
  },
  {
    title: 'Re-engage — lost or gone quiet',
    category: 'Re-engage',
    audience: 'Any contact',
    description: 'Check back in after 60–90 days.',
    subjectLines: ['Checking back in, [Contact Name]', 'Anything changed for [Town Name]?'],
    body: `Hi [Contact Name],

It's been a few months since we talked about an app for [Town Name], so I wanted to check in. We've added new features since then, and your sample is still up: [Sample App Link]

If communications are still on your list for this year, I'd be glad to show you what's new in 15 minutes.

Best,
[Your Full Name]
[Your Phone]`,
  },
]